import { useCallback, useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import { Button, Input, Spinner, Textarea, cn } from '@klaro7/ui';
import {
  SEO_PAGE_KEYS,
  type SeoConfig,
  type SeoPageKey,
  type SeoPageMeta,
} from '@klaro7/shared-types';

import { extractErrorMessage } from '@/lib/api';
import { useSeoConfig, useUpdateSeoConfig } from '@/features/seo/hooks/useSeoConfig';
import { resolveSeoTemplate, truncateDescription } from '@/features/seo/lib/seoUtils';
import {
  buildConsultantJsonLd,
  buildOrganizationJsonLd,
  buildWebSiteJsonLd,
} from '@/features/seo/lib/jsonLd';
import { auditSeoConfig, type AuditFinding, type AuditLevel } from '@/features/seo/lib/seoAudit';
import { CharCount, OgImageField } from '@/features/seo/components/AdminSeoWidgets';
import {
  SeoPreviewPanel,
  type JsonLdPreviewBlock,
  type SerpPreviewData,
  type SocialPreviewData,
} from '@/features/seo/components/SeoPreviewPanel';
import {
  AuditFindingsPanel,
  SeoHeroBanner,
  type SeoHeroStat,
} from '@/features/seo/components/SeoHealth';
import { computeSeoScore } from '@/features/seo/lib/seoAudit';
import { useFeaturedProfessionals } from '@/features/professionals/hooks/usePublicProfessionals';

const PAGE_LABELS: Record<SeoPageKey, string> = {
  '/': 'Home',
  '/consulenti': 'Elenco consulenti',
  '/guide': 'Guida ai primi passi',
  '/faq': 'Domande frequenti',
  '/chi-siamo': 'Chi siamo',
  '/carta-valori': 'Carta dei valori',
  '/codice-etico': 'Codice etico',
  '/come-selezioniamo-i-consulenti': 'Come selezioniamo i consulenti',
  '/lavora-con-noi': 'Lavora con noi',
  '/condizioni-generali': 'Condizioni generali',
  '/privacy': 'Privacy policy',
  '/cookie-policy': 'Cookie policy',
  '/note-legali': 'Note legali',
  '/risoluzione-controversie': 'Risoluzione controversie',
  '/policy-recensioni': 'Policy recensioni',
};

type Section = 'generale' | 'pagine' | 'consulenti' | 'jsonld' | 'verifica';

interface PreviewState {
  serp: SerpPreviewData;
  social: SocialPreviewData;
  jsonLdBlocks: JsonLdPreviewBlock[];
}

function keywordsToString(keywords?: string[]): string {
  return (keywords ?? []).join(', ');
}

function stringToKeywords(value: string): string[] {
  return value
    .split(',')
    .map((k) => k.trim())
    .filter(Boolean)
    .slice(0, 20);
}

function areaToSection(area: string): Exclude<Section, 'verifica'> {
  if (area === 'Generale') return 'generale';
  if (area.startsWith('Pagina ')) return 'pagine';
  if (area === 'Template consulenti') return 'consulenti';
  return 'jsonld';
}

function worstLevel(findings: AuditFinding[]): AuditLevel {
  if (findings.some((f) => f.level === 'error')) return 'error';
  if (findings.some((f) => f.level === 'warn')) return 'warn';
  return 'ok';
}

/* -------------------------------------------------------------------------- */
/*  Rail icons (inline, no external deps)                                     */
/* -------------------------------------------------------------------------- */

type IconProps = { className?: string };

function IconGenerale({ className }: IconProps) {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.5} className={className}>
      <circle cx="12" cy="12" r="8.25" />
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M3.75 12h16.5M12 3.75c2.4 2.4 3.6 5.4 3.6 8.25s-1.2 5.85-3.6 8.25c-2.4-2.4-3.6-5.4-3.6-8.25S9.6 6.15 12 3.75z"
      />
    </svg>
  );
}

function IconPagine({ className }: IconProps) {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.5} className={className}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M7 3.75h7.5L19 8.25V19.5a.75.75 0 01-.75.75H7a.75.75 0 01-.75-.75V4.5A.75.75 0 017 3.75z" />
      <path strokeLinecap="round" strokeLinejoin="round" d="M14 3.75V8.25h4.5" />
      <path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75h6M9 15.75h6M9 9.75h2.25" />
    </svg>
  );
}

function IconConsulenti({ className }: IconProps) {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.5} className={className}>
      <circle cx="12" cy="8.25" r="3.25" />
      <path strokeLinecap="round" strokeLinejoin="round" d="M4.5 19.5c0-3.45 3.36-6 7.5-6s7.5 2.55 7.5 6" />
    </svg>
  );
}

function IconJsonLd({ className }: IconProps) {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.5} className={className}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M8.25 6.75L3.75 12l4.5 5.25M15.75 6.75l4.5 5.25-4.5 5.25" />
    </svg>
  );
}

function IconVerifica({ className }: IconProps) {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.5} className={className}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M12 3l7 3v5.25c0 4.5-3 7.5-7 9-4-1.5-7-4.5-7-9V6l7-3z" />
      <path strokeLinecap="round" strokeLinejoin="round" d="M9 12l2.25 2.25L15.5 9.5" />
    </svg>
  );
}

const SECTION_META: { key: Section; label: string; icon: (p: IconProps) => JSX.Element }[] = [
  { key: 'generale', label: 'Generale', icon: IconGenerale },
  { key: 'pagine', label: 'Pagine', icon: IconPagine },
  { key: 'consulenti', label: 'Consulenti', icon: IconConsulenti },
  { key: 'jsonld', label: 'Dati strutturati', icon: IconJsonLd },
  { key: 'verifica', label: 'Verifica', icon: IconVerifica },
];

const DOT_CLASS: Record<AuditLevel, string> = {
  ok: 'bg-emerald-500',
  warn: 'bg-amber-500',
  error: 'bg-red-500',
};

/* -------------------------------------------------------------------------- */
/*  Root page                                                                  */
/* -------------------------------------------------------------------------- */

export function AdminSeoPage() {
  const { data: config, isLoading } = useSeoConfig();
  const { data: featured } = useFeaturedProfessionals();
  const [activeSection, setActiveSection] = useState<Section>('generale');
  const [preview, setPreview] = useState<PreviewState | null>(null);
  const handlePreviewChange = useCallback((next: PreviewState) => setPreview(next), []);

  const origin = window.location.origin;
  const findings = useMemo(
    () => (config ? auditSeoConfig(config, featured?.[0], origin) : []),
    [config, featured, origin]
  );

  const sectionStatus = useMemo(() => {
    const bucket: Record<Exclude<Section, 'verifica'>, AuditFinding[]> = {
      generale: [],
      pagine: [],
      consulenti: [],
      jsonld: [],
    };
    for (const finding of findings) bucket[areaToSection(finding.area)].push(finding);
    return {
      generale: worstLevel(bucket.generale),
      pagine: worstLevel(bucket.pagine),
      consulenti: worstLevel(bucket.consulenti),
      jsonld: worstLevel(bucket.jsonld),
      verifica: worstLevel(findings),
    } satisfies Record<Section, AuditLevel>;
  }, [findings]);

  const score = useMemo(() => computeSeoScore(findings), [findings]);

  const heroStats: SeoHeroStat[] = useMemo(() => {
    if (!config) return [];
    const errors = findings.filter((f) => f.level === 'error').length;
    const warns = findings.filter((f) => f.level === 'warn').length;
    const pagesWithOwnDescription = SEO_PAGE_KEYS.filter((k) => !!config.pages[k]?.description).length;
    const activeJsonLdBlocks = [
      config.jsonLd.organizationEnabled,
      config.jsonLd.websiteEnabled,
      config.jsonLd.consultantPersonEnabled,
      config.jsonLd.faqPageEnabled,
    ].filter(Boolean).length;
    return [
      { label: 'pagine gestite', value: SEO_PAGE_KEYS.length },
      { label: 'con description propria', value: pagesWithOwnDescription },
      { label: 'blocchi JSON-LD attivi', value: activeJsonLdBlocks },
      {
        label: 'avvisi da correggere',
        value: errors + warns,
        tone: errors > 0 ? 'error' : warns > 0 ? 'warn' : 'ok',
      },
    ];
  }, [config, findings]);

  if (isLoading || !config) {
    return (
      <div className="flex justify-center py-16">
        <Spinner size="lg" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <SeoHeroBanner
        score={score}
        stats={heroStats}
        subtitle="Titoli, descrizioni e anteprime di condivisione delle pagine pubbliche"
      />

      <div className="grid gap-6 xl:grid-cols-[220px_minmax(0,1fr)_380px]">
        <nav className="flex gap-1.5 overflow-x-auto pb-1 xl:w-[220px] xl:shrink-0 xl:flex-col xl:overflow-visible xl:pb-0">
          {SECTION_META.map(({ key, label, icon: Icon }) => {
            const active = activeSection === key;
            return (
              <button
                key={key}
                type="button"
                onClick={() => setActiveSection(key)}
                className={cn(
                  'flex shrink-0 items-center gap-2.5 whitespace-nowrap rounded-lg border-l-2 px-3 py-2.5 text-sm transition-colors xl:w-full',
                  active
                    ? 'border-brand-teal bg-brand-teal-50 font-medium text-brand-teal-700'
                    : 'border-transparent text-neutral-600 hover:bg-neutral-50 hover:text-neutral-900'
                )}
              >
                <Icon className="h-4 w-4 shrink-0" />
                <span className="flex-1 text-left">{label}</span>
                <span className={cn('h-1.5 w-1.5 shrink-0 rounded-full', DOT_CLASS[sectionStatus[key]])} />
              </button>
            );
          })}
        </nav>

        <div key={activeSection} className={cn('min-w-0 animate-fade-in', activeSection === 'verifica' && 'xl:col-span-2')}>
          {activeSection === 'generale' && <GeneraleSection config={config} onPreviewChange={handlePreviewChange} />}
          {activeSection === 'pagine' && <PagineSection config={config} onPreviewChange={handlePreviewChange} />}
          {activeSection === 'consulenti' && <ConsulentiSection config={config} onPreviewChange={handlePreviewChange} />}
          {activeSection === 'jsonld' && <JsonLdSection config={config} onPreviewChange={handlePreviewChange} />}
          {activeSection === 'verifica' && <VerificaSection findings={findings} origin={origin} />}
        </div>

        {activeSection !== 'verifica' && preview && (
          <aside className="xl:sticky xl:top-6 xl:self-start">
            <SeoPreviewPanel serp={preview.serp} social={preview.social} jsonLdBlocks={preview.jsonLdBlocks} />
          </aside>
        )}
      </div>
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/*  Shared editor primitives                                                  */
/* -------------------------------------------------------------------------- */

function SectionCard({
  title,
  description,
  children,
}: {
  title: string;
  description?: string;
  children: React.ReactNode;
}) {
  return (
    <div className="rounded-2xl border border-neutral-200 bg-white p-6">
      <div className="mb-5">
        <h2 className="text-base font-semibold text-neutral-900">{title}</h2>
        {description && <p className="mt-1 text-sm text-neutral-500">{description}</p>}
      </div>
      <div className="space-y-4">{children}</div>
    </div>
  );
}

function ToggleRow({
  label,
  description,
  checked,
  onChange,
}: {
  label: string;
  description: string;
  checked: boolean;
  onChange: (value: boolean) => void;
}) {
  return (
    <label className="flex items-start gap-3 rounded-lg border border-neutral-100 p-3 hover:bg-neutral-50">
      <input
        type="checkbox"
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
        className="mt-0.5 h-4 w-4 rounded border-neutral-300 text-brand-teal focus:ring-brand-teal"
      />
      <span>
        <span className="block text-sm font-medium text-neutral-800">{label}</span>
        <span className="block text-xs text-neutral-500">{description}</span>
      </span>
    </label>
  );
}

function ExternalValidators({ origin }: { origin: string }) {
  const prodUrl = origin.includes('localhost') ? 'https://app.klaro7.com' : origin;
  return (
    <div className="rounded-2xl border border-neutral-200 bg-white p-6">
      <h3 className="mb-1 text-sm font-semibold text-neutral-700">Validatori ufficiali</h3>
      <p className="mb-3 text-xs text-neutral-500">
        Verifica finale sui servizi di Google e Schema.org (aprono in una nuova scheda, testano il sito pubblicato).
      </p>
      <div className="flex flex-wrap gap-2">
        <a
          href={`https://search.google.com/test/rich-results?url=${encodeURIComponent(prodUrl)}`}
          target="_blank"
          rel="noreferrer"
          className="rounded-lg bg-brand-teal px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-brand-teal/90"
        >
          Google Rich Results Test
        </a>
        <a
          href={`https://validator.schema.org/#url=${encodeURIComponent(prodUrl)}`}
          target="_blank"
          rel="noreferrer"
          className="rounded-lg bg-neutral-100 px-4 py-2 text-sm font-medium text-neutral-700 transition-colors hover:bg-neutral-200"
        >
          Schema.org Validator
        </a>
        <a
          href={`${prodUrl}/sitemap.xml`}
          target="_blank"
          rel="noreferrer"
          className="rounded-lg bg-neutral-100 px-4 py-2 text-sm font-medium text-neutral-700 transition-colors hover:bg-neutral-200"
        >
          Apri sitemap.xml
        </a>
        <a
          href={`${prodUrl}/robots.txt`}
          target="_blank"
          rel="noreferrer"
          className="rounded-lg bg-neutral-100 px-4 py-2 text-sm font-medium text-neutral-700 transition-colors hover:bg-neutral-200"
        >
          Apri robots.txt
        </a>
      </div>
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/*  Sezione: Generale                                                          */
/* -------------------------------------------------------------------------- */

function GeneraleSection({
  config,
  onPreviewChange,
}: {
  config: SeoConfig;
  onPreviewChange: (p: PreviewState) => void;
}) {
  const updateMutation = useUpdateSeoConfig();
  const [form, setForm] = useState(config.global);
  const [keywords, setKeywords] = useState(keywordsToString(config.global.defaultKeywords));

  useEffect(() => {
    setForm(config.global);
    setKeywords(keywordsToString(config.global.defaultKeywords));
  }, [config.global]);

  const origin = window.location.origin;
  const previewTitle = `${form.defaultTitle}${form.titleSuffix}`;

  useEffect(() => {
    onPreviewChange({
      serp: { title: previewTitle, description: form.defaultDescription, path: '/' },
      social: {
        title: previewTitle,
        description: form.defaultDescription,
        imageUrl: form.defaultOgImage,
        siteName: form.siteName,
      },
      jsonLdBlocks: [
        { label: 'Organization', data: buildOrganizationJsonLd(config, origin) },
        { label: 'WebSite', data: buildWebSiteJsonLd(config, origin) },
      ],
    });
  }, [previewTitle, form.defaultDescription, form.defaultOgImage, form.siteName, config, origin, onPreviewChange]);

  async function handleSave() {
    try {
      await updateMutation.mutateAsync({
        global: { ...form, defaultKeywords: stringToKeywords(keywords) },
      });
      toast.success('Impostazioni SEO generali salvate');
    } catch (err) {
      toast.error(extractErrorMessage(err));
    }
  }

  return (
    <SectionCard title="Generale" description="Default del sito, usati quando una pagina non ha valori propri.">
      <div className="grid gap-4 md:grid-cols-2">
        <Input
          label="Nome del sito"
          value={form.siteName}
          onChange={(e) => setForm({ ...form, siteName: e.target.value })}
        />
        <Input
          label="Suffisso titolo (aggiunto ai titoli di pagina)"
          placeholder=" | Klaro7"
          value={form.titleSuffix}
          onChange={(e) => setForm({ ...form, titleSuffix: e.target.value })}
        />
      </div>
      <div>
        <Input
          label="Meta title di default"
          value={form.defaultTitle}
          onChange={(e) => setForm({ ...form, defaultTitle: e.target.value })}
        />
        <CharCount value={form.defaultTitle} ideal={60} />
      </div>
      <div>
        <Textarea
          label="Meta description di default"
          rows={3}
          value={form.defaultDescription}
          onChange={(e) => setForm({ ...form, defaultDescription: e.target.value })}
        />
        <CharCount value={form.defaultDescription} ideal={160} />
      </div>
      <Input
        label="Keywords di default (separate da virgola)"
        placeholder="cartomanzia, tarocchi, astrologia"
        value={keywords}
        onChange={(e) => setKeywords(e.target.value)}
      />
      <OgImageField
        value={form.defaultOgImage ?? ''}
        onChange={(url) => setForm({ ...form, defaultOgImage: url || undefined })}
      />
      <div className="flex justify-end border-t border-neutral-100 pt-4">
        <Button onClick={handleSave} loading={updateMutation.isPending}>
          Salva impostazioni generali
        </Button>
      </div>
    </SectionCard>
  );
}

/* -------------------------------------------------------------------------- */
/*  Sezione: Pagine                                                           */
/* -------------------------------------------------------------------------- */

function PagineSection({
  config,
  onPreviewChange,
}: {
  config: SeoConfig;
  onPreviewChange: (p: PreviewState) => void;
}) {
  const updateMutation = useUpdateSeoConfig();
  const [selectedKey, setSelectedKey] = useState<SeoPageKey>('/');
  const [meta, setMeta] = useState<SeoPageMeta>(config.pages[selectedKey] ?? {});
  const [keywords, setKeywords] = useState(keywordsToString(config.pages[selectedKey]?.keywords));

  useEffect(() => {
    const pageMeta = config.pages[selectedKey] ?? {};
    setMeta(pageMeta);
    setKeywords(keywordsToString(pageMeta.keywords));
  }, [config.pages, selectedKey]);

  const previewTitle = `${meta.title || config.global.defaultTitle}${config.global.titleSuffix}`;
  const previewDescription = meta.description || config.global.defaultDescription;
  const previewImage = meta.ogImage || config.global.defaultOgImage;

  useEffect(() => {
    onPreviewChange({
      serp: { title: previewTitle, description: previewDescription, path: selectedKey },
      social: {
        title: previewTitle,
        description: previewDescription,
        imageUrl: previewImage,
        siteName: config.global.siteName,
      },
      jsonLdBlocks: [],
    });
  }, [previewTitle, previewDescription, previewImage, selectedKey, config.global.siteName, onPreviewChange]);

  async function handleSave() {
    try {
      await updateMutation.mutateAsync({
        pages: { [selectedKey]: { ...meta, keywords: stringToKeywords(keywords) } },
      });
      toast.success(`SEO di "${PAGE_LABELS[selectedKey]}" salvata`);
    } catch (err) {
      toast.error(extractErrorMessage(err));
    }
  }

  return (
    <SectionCard title="Pagine" description="Meta title, description e opzioni SEO per ogni pagina pubblica.">
      <div className="-mt-1 flex gap-1.5 overflow-x-auto pb-1">
        {SEO_PAGE_KEYS.map((key) => {
          const hasCustom = !!(config.pages[key]?.title || config.pages[key]?.description);
          const active = selectedKey === key;
          return (
            <button
              key={key}
              type="button"
              onClick={() => setSelectedKey(key)}
              className={cn(
                'flex shrink-0 items-center gap-1.5 whitespace-nowrap rounded-full border px-3 py-1.5 text-xs font-medium transition-colors',
                active
                  ? 'border-brand-teal bg-brand-teal-50 text-brand-teal-700'
                  : 'border-neutral-200 bg-white text-neutral-600 hover:border-neutral-300 hover:bg-neutral-50'
              )}
            >
              {PAGE_LABELS[key]}
              {config.pages[key]?.noindex && (
                <span className="rounded bg-amber-100 px-1 py-0.5 text-[9px] font-semibold text-amber-700">
                  noindex
                </span>
              )}
              {hasCustom && <span className="h-1.5 w-1.5 rounded-full bg-brand-teal" />}
            </button>
          );
        })}
      </div>

      <div className="flex items-center justify-between">
        <span className="text-xs text-neutral-500">Pagina selezionata</span>
        <span className="rounded bg-neutral-100 px-2 py-0.5 font-mono text-xs text-neutral-500">
          {selectedKey}
        </span>
      </div>

      <div>
        <Input
          label="Meta title"
          placeholder={config.global.defaultTitle}
          value={meta.title ?? ''}
          onChange={(e) => setMeta({ ...meta, title: e.target.value })}
        />
        <CharCount value={meta.title ?? ''} ideal={60} />
      </div>
      <div>
        <Textarea
          label="Meta description"
          placeholder={config.global.defaultDescription}
          rows={3}
          value={meta.description ?? ''}
          onChange={(e) => setMeta({ ...meta, description: e.target.value })}
        />
        <CharCount value={meta.description ?? ''} ideal={160} />
      </div>
      <Input
        label="Keywords (separate da virgola, vuoto = default)"
        value={keywords}
        onChange={(e) => setKeywords(e.target.value)}
      />
      <OgImageField
        value={meta.ogImage ?? ''}
        onChange={(url) => setMeta({ ...meta, ogImage: url || undefined })}
      />
      <label className="flex items-center gap-2 text-sm text-neutral-700">
        <input
          type="checkbox"
          checked={meta.noindex ?? false}
          onChange={(e) => setMeta({ ...meta, noindex: e.target.checked })}
          className="h-4 w-4 rounded border-neutral-300 text-brand-teal focus:ring-brand-teal"
        />
        Non indicizzare questa pagina (noindex, esclusa dalla sitemap)
      </label>
      <div className="flex justify-end border-t border-neutral-100 pt-4">
        <Button onClick={handleSave} loading={updateMutation.isPending}>
          Salva pagina
        </Button>
      </div>
    </SectionCard>
  );
}

/* -------------------------------------------------------------------------- */
/*  Sezione: Consulenti                                                        */
/* -------------------------------------------------------------------------- */

const SAMPLE_VALUES = {
  nome: 'Luna Stellare',
  titolo: 'Cartomante e astrologa',
  specializzazioni: 'Tarocchi, Astrologia',
  bio: 'Da oltre dieci anni accompagno le persone nei momenti di dubbio con letture accurate e rispettose.',
};

function ConsulentiSection({
  config,
  onPreviewChange,
}: {
  config: SeoConfig;
  onPreviewChange: (p: PreviewState) => void;
}) {
  const updateMutation = useUpdateSeoConfig();
  const { data: featured } = useFeaturedProfessionals();
  const [form, setForm] = useState(config.consultant);

  useEffect(() => {
    setForm(config.consultant);
  }, [config.consultant]);

  const samplePro = featured?.[0];

  // Live preview on a real published consultant when available.
  const previewValues = useMemo(() => {
    if (!samplePro) return SAMPLE_VALUES;
    const profile = samplePro.userId?.profile;
    return {
      nome: `${profile?.firstName ?? ''} ${profile?.lastName ?? ''}`.trim() || SAMPLE_VALUES.nome,
      titolo: samplePro.title,
      specializzazioni: samplePro.specializations.join(', '),
      bio: (samplePro.profileDescription ?? '').replace(/<[^>]+>/g, ' ').replace(/\s+/g, ' ').trim(),
    };
  }, [samplePro]);

  const previewTitle = `${resolveSeoTemplate(form.titleTemplate, previewValues)}${config.global.titleSuffix}`;
  const previewDescription = truncateDescription(
    resolveSeoTemplate(form.descriptionTemplate, previewValues),
    160
  );
  const origin = window.location.origin;
  const previewConfig: SeoConfig = { ...config, consultant: form };

  useEffect(() => {
    const personBlock = samplePro
      ? buildConsultantJsonLd(previewConfig, samplePro, `${origin}/consultant/${samplePro.id}`)
      : null;
    onPreviewChange({
      serp: {
        title: previewTitle,
        description: previewDescription,
        path: samplePro ? `/consultant/${samplePro.id}` : '/consultant/…',
      },
      social: {
        title: previewTitle,
        description: previewDescription,
        imageUrl: config.global.defaultOgImage,
        siteName: config.global.siteName,
      },
      jsonLdBlocks: [
        {
          label: samplePro ? `Person — ${samplePro.title}` : 'Person (nessun consulente pubblicato)',
          data: personBlock,
        },
      ],
    });
    // previewConfig is derived fresh each render from config+form; only its
    // effective inputs need tracking here to avoid redundant preview pushes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [previewTitle, previewDescription, samplePro, origin, config.global.defaultOgImage, config.global.siteName, onPreviewChange]);

  async function handleSave() {
    try {
      await updateMutation.mutateAsync({ consultant: form });
      toast.success('Template schede consulente salvato');
    } catch (err) {
      toast.error(extractErrorMessage(err));
    }
  }

  return (
    <SectionCard
      title="Consulenti"
      description="Ogni profilo pubblico genera titolo e descrizione da questi modelli."
    >
      <p className="-mt-2 text-xs text-neutral-500">
        Segnaposto disponibili:{' '}
        <code className="rounded bg-neutral-100 px-1">{'{nome}'}</code>{' '}
        <code className="rounded bg-neutral-100 px-1">{'{titolo}'}</code>{' '}
        <code className="rounded bg-neutral-100 px-1">{'{specializzazioni}'}</code>{' '}
        <code className="rounded bg-neutral-100 px-1">{'{bio}'}</code>
      </p>
      <div>
        <Input
          label="Template titolo"
          value={form.titleTemplate}
          onChange={(e) => setForm({ ...form, titleTemplate: e.target.value })}
        />
        <CharCount value={resolveSeoTemplate(form.titleTemplate, previewValues)} ideal={60} />
      </div>
      <div>
        <Textarea
          label="Template descrizione"
          rows={3}
          value={form.descriptionTemplate}
          onChange={(e) => setForm({ ...form, descriptionTemplate: e.target.value })}
        />
        <CharCount value={resolveSeoTemplate(form.descriptionTemplate, previewValues)} ideal={160} />
      </div>
      <p className="text-xs text-neutral-500">
        {samplePro
          ? 'Anteprima calcolata su un consulente pubblicato reale.'
          : 'Anteprima calcolata su dati di esempio.'}
      </p>
      <div className="flex justify-end border-t border-neutral-100 pt-4">
        <Button onClick={handleSave} loading={updateMutation.isPending}>
          Salva template
        </Button>
      </div>
    </SectionCard>
  );
}

/* -------------------------------------------------------------------------- */
/*  Sezione: Dati strutturati (JSON-LD)                                       */
/* -------------------------------------------------------------------------- */

function JsonLdSection({
  config,
  onPreviewChange,
}: {
  config: SeoConfig;
  onPreviewChange: (p: PreviewState) => void;
}) {
  const updateMutation = useUpdateSeoConfig();
  const { data: featured } = useFeaturedProfessionals();
  const [form, setForm] = useState(config.jsonLd);
  const [sameAsText, setSameAsText] = useState(config.jsonLd.organizationSameAs.join('\n'));

  useEffect(() => {
    setForm(config.jsonLd);
    setSameAsText(config.jsonLd.organizationSameAs.join('\n'));
  }, [config.jsonLd]);

  const origin = window.location.origin;
  const previewConfig: SeoConfig = useMemo(
    () => ({
      ...config,
      jsonLd: {
        ...form,
        organizationSameAs: sameAsText.split('\n').map((s) => s.trim()).filter(Boolean),
      },
    }),
    [config, form, sameAsText]
  );
  const samplePro = featured?.[0];

  useEffect(() => {
    const defaultTitle = `${config.global.defaultTitle}${config.global.titleSuffix}`;
    onPreviewChange({
      serp: { title: defaultTitle, description: config.global.defaultDescription, path: '/' },
      social: {
        title: defaultTitle,
        description: config.global.defaultDescription,
        imageUrl: config.global.defaultOgImage,
        siteName: config.global.siteName,
      },
      jsonLdBlocks: [
        { label: 'Organization', data: buildOrganizationJsonLd(previewConfig, origin) },
        { label: 'WebSite', data: buildWebSiteJsonLd(previewConfig, origin) },
        {
          label: samplePro ? `Person — ${samplePro.title}` : 'Person (nessun consulente pubblicato)',
          data: samplePro
            ? buildConsultantJsonLd(previewConfig, samplePro, `${origin}/consultant/${samplePro.id}`)
            : null,
        },
      ],
    });
  }, [previewConfig, origin, samplePro, config.global, onPreviewChange]);

  async function handleSave() {
    try {
      await updateMutation.mutateAsync({ jsonLd: previewConfig.jsonLd });
      toast.success('Dati strutturati salvati');
    } catch (err) {
      toast.error(extractErrorMessage(err));
    }
  }

  return (
    <div className="space-y-6">
      <SectionCard
        title="Dati strutturati"
        description="Informazioni in formato Schema.org che aiutano Google a mostrare risultati arricchiti (logo aziendale, valutazioni con stelle, FAQ espandibili)."
      >
        <ToggleRow
          label="Organization (sito)"
          description="Nome, logo e profili social dell'azienda — emesso in home page"
          checked={form.organizationEnabled}
          onChange={(v) => setForm({ ...form, organizationEnabled: v })}
        />
        {form.organizationEnabled && (
          <div className="ml-7 space-y-4">
            <Input
              label="Nome organizzazione"
              value={form.organizationName}
              onChange={(e) => setForm({ ...form, organizationName: e.target.value })}
            />
            <OgImageField
              label="Logo (URL)"
              value={form.organizationLogo ?? ''}
              onChange={(url) => setForm({ ...form, organizationLogo: url || undefined })}
            />
            <Textarea
              label="Profili social (un URL per riga — Instagram, Facebook, ecc.)"
              rows={3}
              placeholder={'https://www.instagram.com/klaro7\nhttps://www.facebook.com/klaro7'}
              value={sameAsText}
              onChange={(e) => setSameAsText(e.target.value)}
            />
          </div>
        )}
        <ToggleRow
          label="WebSite"
          description="Nome e lingua del sito — emesso in home page"
          checked={form.websiteEnabled}
          onChange={(v) => setForm({ ...form, websiteEnabled: v })}
        />
        <ToggleRow
          label="Person (schede consulente)"
          description="Nome, foto, specializzazioni e valutazione a stelle di ogni consulente pubblicato"
          checked={form.consultantPersonEnabled}
          onChange={(v) => setForm({ ...form, consultantPersonEnabled: v })}
        />
        <ToggleRow
          label="FAQPage (pagina FAQ)"
          description="Domande e risposte della pagina FAQ — idoneo alle FAQ espandibili in SERP"
          checked={form.faqPageEnabled}
          onChange={(v) => setForm({ ...form, faqPageEnabled: v })}
        />
        <div className="flex justify-end border-t border-neutral-100 pt-4">
          <Button onClick={handleSave} loading={updateMutation.isPending}>
            Salva dati strutturati
          </Button>
        </div>
      </SectionCard>

      <div className="rounded-2xl border border-neutral-200 bg-white p-6">
        <p className="mb-2 text-xs font-medium uppercase tracking-wide text-neutral-400">FAQPage</p>
        <p className="text-sm text-neutral-600">
          Generato automaticamente dalle domande (titoli) della pagina FAQ pubblica quando attivo.
          Verificalo dal vivo con i validatori qui sotto aprendo <code className="rounded bg-neutral-100 px-1">/faq</code>.
        </p>
      </div>

      <ExternalValidators origin={origin} />
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/*  Sezione: Verifica (audit automatico)                                      */
/* -------------------------------------------------------------------------- */

function VerificaSection({ findings, origin }: { findings: AuditFinding[]; origin: string }) {
  const [showOk, setShowOk] = useState(false);

  return (
    <div className="space-y-6">
      <AuditFindingsPanel findings={findings} showOk={showOk} onToggleOk={setShowOk} />
      <ExternalValidators origin={origin} />
    </div>
  );
}
